import React, { useCallback, useEffect, useState } from 'react';
import prestacaoContaService from '../services/prestacaoContaService';
import Button from './ui/Button';
import { FIELD_CLASS } from './ui/fieldStyles';
import type {
  PrestacaoPcConfig,
  PrestacaoPcModalidade,
  PrestacaoPcPeriodicidade,
} from '../services/prestacaoContaService';

const LIMITE_DIAS_PC_UNICA = 180;

const formatarData = (iso?: string | null) => {
  if (!iso) return '—';
  const [ano, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${ano}`;
};

const erroDaApi = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { error?: string } } })?.response?.data?.error || fallback;

type Props = {
  planoId: string;
  /** Avisa a tela se a definição já está gravada (libera a conclusão da formalização). */
  onConfiguradoChange?: (configurado: boolean) => void;
};

/** Definição das PCs na formalização do termo de fomento (Convênios). */
const PrestacaoPcConfigPanel: React.FC<Props> = ({ planoId, onConfiguradoChange }) => {
  const [config, setConfig] = useState<PrestacaoPcConfig | null>(null);
  const [modalidade, setModalidade] = useState<PrestacaoPcModalidade | ''>('');
  const [periodicidade, setPeriodicidade] = useState<PrestacaoPcPeriodicidade | ''>('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const aplicar = useCallback(
    (c: PrestacaoPcConfig) => {
      setConfig(c);
      setModalidade(c.modalidade ?? '');
      setPeriodicidade(c.periodicidade ?? '');
      onConfiguradoChange?.(!!c.modalidade && c.periodos.length > 0);
    },
    [onConfiguradoChange],
  );

  useEffect(() => {
    let ativo = true;
    prestacaoContaService
      .getConfiguracaoPcs(planoId)
      .then((c) => ativo && aplicar(c))
      .catch((e) => ativo && setErro(erroDaApi(e, 'Não foi possível carregar a definição das PCs.')));
    return () => {
      ativo = false;
    };
  }, [planoId, aplicar]);

  const salvar = async () => {
    if (!modalidade) return;
    setSalvando(true);
    setErro(null);
    try {
      aplicar(
        await prestacaoContaService.salvarConfiguracaoPcs(planoId, {
          modalidade,
          periodicidade: modalidade === 'PARCIAL' ? periodicidade || null : null,
        }),
      );
    } catch (e) {
      setErro(erroDaApi(e, 'Não foi possível salvar a definição das PCs.'));
    } finally {
      setSalvando(false);
    }
  };

  if (!config) {
    return erro ? <p className="text-sm text-red-600">{erro}</p> : null;
  }

  const dias = config.vigenciaDias ?? null;
  const unicaPermitida = dias != null && dias < LIMITE_DIAS_PC_UNICA;
  const editavel = config.podeEditar;
  const podeSalvar = editavel && !!modalidade && (modalidade === 'UNICA' || !!periodicidade);

  return (
    <div className="w-full space-y-3 rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm dark:border-slate-700 dark:bg-slate-950">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-fg-muted">Prestação de contas</p>
        <p className="text-sm text-gray-700 dark:text-slate-300">
          Vigência: <strong>{formatarData(config.vigenciaInicio)}</strong> a{' '}
          <strong>{formatarData(config.vigenciaFim)}</strong>
          {dias != null && <> ({dias} dias)</>}
        </p>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <label className="space-y-1 text-sm text-gray-700 dark:text-slate-300">
          <span className="block text-xs font-semibold text-fg-muted">Modalidade</span>
          <select
            value={modalidade}
            disabled={!editavel}
            onChange={(e) => setModalidade(e.target.value as PrestacaoPcModalidade | '')}
            className={FIELD_CLASS}
          >
            <option value="">Selecionar...</option>
            <option value="PARCIAL">Parcial (bimestral ou quadrimestral)</option>
            <option value="UNICA" disabled={!unicaPermitida}>
              Única{unicaPermitida ? '' : ` (só com vigência inferior a ${LIMITE_DIAS_PC_UNICA} dias)`}
            </option>
          </select>
        </label>

        {modalidade === 'PARCIAL' && (
          <label className="space-y-1 text-sm text-gray-700 dark:text-slate-300">
            <span className="block text-xs font-semibold text-fg-muted">Periodicidade</span>
            <select
              value={periodicidade}
              disabled={!editavel}
              onChange={(e) => setPeriodicidade(e.target.value as PrestacaoPcPeriodicidade | '')}
              className={FIELD_CLASS}
            >
              <option value="">Selecionar...</option>
              <option value="BIMESTRAL">Bimestral</option>
              <option value="QUADRIMESTRAL">Quadrimestral</option>
            </select>
          </label>
        )}
      </div>

      {config.periodos.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-gray-700 dark:text-slate-300">
            <thead className="text-fg-muted">
              <tr>
                <th className="py-1 pr-3">PC</th>
                <th className="py-1 pr-3">Período de referência</th>
                <th className="py-1">Prazo de entrega</th>
              </tr>
            </thead>
            <tbody>
              {config.periodos.map((p) => (
                <tr key={p.numero} className="border-t border-gray-100 dark:border-slate-800">
                  <td className="py-1 pr-3">
                    {p.numero}ª{p.ehFinal ? ' (final)' : ''}
                  </td>
                  <td className="py-1 pr-3">
                    {formatarData(p.inicio)} a {formatarData(p.fim)}
                  </td>
                  <td className="py-1">{formatarData(p.prazo)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {erro && <p className="text-sm text-red-600">{erro}</p>}

      {editavel ? (
        <div className="flex items-center gap-3">
          <Button variant="primary" size="sm" onClick={salvar} disabled={!podeSalvar || salvando}>
            {salvando ? 'Salvando...' : 'Salvar definição'}
          </Button>
          <p className="text-xs text-fg-muted">
            Obrigatório para concluir a formalização. As PCs são criadas ao concluir.
          </p>
        </div>
      ) : (
        config.pcsGeradas && <p className="text-xs text-fg-muted">PCs já criadas; a definição não pode mais ser alterada.</p>
      )}
    </div>
  );
};

export default PrestacaoPcConfigPanel;
